'use client';

import { RecipeVariantProps } from '@pandacss/types';
import { animate as animateValue, useMotionValue } from 'motion/react';
import * as m from 'motion/react-m';
import {
  Children,
  ComponentProps,
  FormHTMLAttributes,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  cloneElement,
  createContext,
  isValidElement,
  use,
  useMemo,
  useRef,
} from 'react';

import { Styles, css, sva } from '@flipster/styled-system/css';
import { center, flex, stack } from '@flipster/styled-system/patterns';

import XMarkIcon from '../../icon/generated/XMarkIcon';
import { useLockScroll } from '../../misc/browser/scroll';
import { Portal } from '../../misc/portal';
import { useBreakpoint } from '../../misc/responsive';
import { Button as RawButton } from '../button';
import { Spinner } from '../spinner';
import { useSwipeToClose } from './useSwipeToClose';

interface ModalContextType {
  slots: ReturnType<typeof modalRecipe.raw>;
}

const ModalContext = createContext<ModalContextType | null>(null);

const useModalContext = () => {
  const ctx = use(ModalContext);
  if (!ctx) throw new Error('Modal components must be used within Modal.Root');
  return ctx;
};

const initial = { opacity: 0.5 };
const animate = { opacity: 1, transition: { ease: 'easeOut' as const, duration: 0.2 } };
const exit = { opacity: 0.5, transition: { ease: 'easeIn' as const, duration: 0.2 } };

interface RootProps {
  size?: ModalVariantProps['size'];
  mode?: ModalVariantProps['mode'];
  // TODO 최대한 modal 에서 form 안쓰는걸로 바꾸고 나중에 제거
  form?: ReactElement<FormHTMLAttributes<HTMLFormElement>>;
  children: ReactNode;
  onClose?: () => void;
}

const Root = ({ size, mode, form, children, onClose }: RootProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  const y = useMotionValue(0);
  const { isMobile } = useBreakpoint();

  const isBottomSheet = mode === 'bottomSheet' && isMobile;
  const [footer, content] = useMemo(() => partitionByDisplayName(children, 'Modal.Footer'), [children]);
  const withFooter = !!footer;
  const slots = useMemo(() => modalRecipe.raw({ size, mode, withFooter }), [size, mode, withFooter]);

  useLockScroll();
  useSwipeToClose(dialogRef, {
    enabled: isBottomSheet,
    threshold: 100,
    velocityThreshold: 0.5,
    onSwipeMove(displacement) {
      y.set(Math.max(0, displacement));
    },
    onSwipeEnd() {
      animateValue(y, 0, { type: 'spring', stiffness: 400, damping: 36 });
    },
    onClose() {
      onClose?.();
    },
  });

  return (
    <Portal selector='#modal'>
      <ModalContext value={{ slots }}>
        <m.div
          role='presentation'
          className={css(slots.overlay)}
          initial={initial}
          animate={animate}
          exit={exit}
          onClick={onClose}
        />
        <m.aside
          ref={dialogRef}
          role='dialog'
          aria-modal='true'
          className={css(slots.dialog)}
          initial={{ ...initial, ...(isBottomSheet ? { y: '100%' } : { scale: 0.8 }) }}
          animate={{ ...animate, ...(isBottomSheet ? { y: 0 } : { scale: 1 }) }}
          exit={{ ...exit, ...(isBottomSheet ? { y: '100%' } : { scale: 0.8 }) }}
          transformTemplate={(transform, generatedTransforms) => {
            if (isBottomSheet) return generatedTransforms;
            return `translate(-50%, -50%) ${generatedTransforms}`;
          }}
          style={isBottomSheet ? { y } : undefined}
          data-drag-handle
        >
          {isBottomSheet && <div className={css(slots.dragHandle)} />}
          {!isBottomSheet && (
            <div className={css(slots.closeButtonWrapper)}>
              <button type='button' className={css(slots.closeButton)} onClick={onClose}>
                <XMarkIcon />
              </button>
            </div>
          )}
          <FormOrChildren form={form}>
            <div className={css(slots.scrollWrapper)}>
              <div className={css(slots.scrollContent)}>{content}</div>
            </div>
            {footer}
          </FormOrChildren>
        </m.aside>
      </ModalContext>
    </Portal>
  );
};

const Header = ({ children }: PropsWithChildren) => {
  const { slots } = useModalContext();
  return <header className={css(slots.header)}>{children}</header>;
};

const Title = ({ icon, label }: { icon?: ReactNode; label: ReactNode }) => {
  return (
    <h2 className={flex({ textStyle: 'Title/M', color: 'content.default.level-4', align: 'center', gap: 8 })}>
      {icon}
      <p className={css({ flex: 1 })}>{label}</p>
    </h2>
  );
};

const Subtitle = ({ icon, label }: { icon?: ReactNode; label: ReactNode }) => {
  return (
    <div className={flex({ textStyle: 'Text/M/Regular', color: 'content.default.level-4', align: 'center', gap: 8 })}>
      {icon}
      <p className={css({ flex: 1 })}>{label}</p>
    </div>
  );
};

const Content = ({ isLoading, css: cssProps, children }: PropsWithChildren<{ css?: Styles; isLoading?: boolean }>) => {
  const { slots } = useModalContext();
  if (isLoading)
    return (
      <div className={css(slots.loading)}>
        <Spinner size={24} />
      </div>
    );
  return <div className={css(slots.content, cssProps)}>{children}</div>;
};

const Footer = ({ align = 'horizontal', children }: PropsWithChildren<{ align?: 'vertical' | 'horizontal' }>) => {
  const { slots } = useModalContext();
  return (
    <footer className={css(slots.footer, { flexDirection: align === 'vertical' ? 'column' : 'row' })}>
      {children}
    </footer>
  );
};

const Button = ({ css: cssProps, ...props }: ComponentProps<typeof RawButton>) => {
  const { slots } = useModalContext();
  return <RawButton css={css.raw(slots.button, cssProps)} {...props} />;
};

Root.displayName = 'Modal.Root';
Header.displayName = 'Modal.Header';
Title.displayName = 'Modal.Title';
Subtitle.displayName = 'Modal.Subtitle';
Content.displayName = 'Modal.Content';
Footer.displayName = 'Modal.Footer';
Button.displayName = 'Modal.Button';
export { Root, Header, Title, Subtitle, Content, Footer, Button };

const modalRecipe = sva({
  className: 'modal',
  slots: [
    'overlay',
    'dialog',
    'dragHandle',
    'closeButtonWrapper',
    'closeButton',
    'scrollWrapper',
    'scrollContent',
    'header',
    'content',
    'loading',
    'footer',
    'button',
  ],
  base: {
    overlay: {
      zIndex: 'modal',
      pos: 'fixed',
      inset: 0,
      bgColor: 'background.interaction.dim',
    },
    dialog: {
      ...stack.raw({
        zIndex: 'modal',
        pos: 'fixed',
        maxW: '100dvw',
        bgColor: 'background.default.level-0',
        boxShadow: { tablet: '0 8px 40px 0 rgba(0, 0, 0, 0.80)' },
        overflow: 'hidden',
        pointerEvents: 'auto',
      }),
    },
    dragHandle: {
      ...center.raw({
        py: 8,
        _after: {
          content: '""',
          h: 4,
          w: 36,
          rounded: 1000,
          bgColor: 'gray.500',
        },
      }),
    },
    closeButtonWrapper: {
      ...flex.raw({
        justify: 'flex-end',
        pt: 16,
        px: 16,
        pb: 4,
      }),
    },
    closeButton: {
      ...center.raw({
        color: 'content.default.level-3',
        _hover: {
          opacity: 0.6,
        },
      }),
    },
    header: {
      display: 'flex',
      flexDirection: 'column',
      rowGap: 8,
      pb: 16,
    },
    scrollWrapper: {
      ...stack.raw({
        pos: 'relative',
        flex: 1,
        overflow: 'hidden',
        _after: {
          content: '""',
          pos: 'absolute',
          left: 0,
          bottom: 0,
          right: 0,
          h: 'var(--scroll-pb)',
          bg: 'linear-gradient(to top, {colors.background.default.level-0/80}, {colors.background.default.level-0/0})',
          pointerEvents: 'none',
        },
      }),
    },
    scrollContent: {
      ...stack.raw({
        flex: 1,
        overflowY: 'auto',
      }),
    },
    content: {
      pb: 'var(--scroll-pb)',
      px: { base: 16, tablet: 40 },
    },
    loading: {
      ...center.raw({
        h: 500,
        maxH: '50dvh',
      }),
    },
    footer: {
      ...center.raw({
        gap: 8,
        px: { base: 16, tablet: 40 },
        pb: { base: 24, tablet: 40 },
      }),
    },
    button: {
      w: '100%',
      maxW: { tablet: 480 },
    },
  },
  defaultVariants: {
    size: 'small',
    mode: 'fullModal',
  },
  variants: {
    size: {
      small: {
        dialog: {
          w: { base: '100dvw', tablet: 560 },
        },
      },
      large: {
        dialog: {
          w: { base: '100dvw', tablet: 800 },
        },
      },
    },
    mode: {
      bottomSheet: {
        dialog: {
          tabletDown: {
            left: 0,
            bottom: 0,
            borderTop: '1px solid',
            borderColor: 'border.default.level-1',
            roundedTop: 24,
            maxH: '80dvh',
          },
          tablet: {
            top: '50%',
            left: '50%',
            border: '1px solid',
            borderColor: 'border.default.level-1',
            rounded: 12,
            maxH: 'calc(100dvh - 80px)',
          },
        },
        header: {
          pt: { tabletDown: 16 },
          pl: { base: 16, tablet: 40 },
          pr: { base: 16, tablet: 80 },
        },
      },
      fullModal: {
        dialog: {
          top: '50%',
          left: '50%',
          h: { tabletDown: '100dvh' },
          maxH: { base: '100dvh', tablet: 'calc(100dvh - 80px)' },
          border: { tablet: '1px solid' },
          borderColor: { tablet: 'border.default.level-1' },
          rounded: { tablet: 12 },
        },
        header: {
          pl: { base: 16, tablet: 40 },
          pr: { base: 64, tablet: 80 },
        },
      },
    },
    withFooter: {
      true: {
        dialog: {
          '--scroll-pb': '32px',
        },
      },
      false: {
        dialog: {
          '--scroll-pb': '40px',
        },
      },
    },
  },
});

const FormOrChildren = ({ form, children }: PropsWithChildren<{ form: RootProps['form'] }>) => {
  if (form && isValidElement(form) && form.type === 'form')
    return cloneElement(form, { children, className: stack({ w: '100%', flex: 1 }) });
  return children;
};

function partitionByDisplayName(children: ReactNode, displayName: string) {
  const arr = Children.toArray(children);
  let matched: ReactNode | null = null;
  const rest: ReactNode[] = [];

  for (const child of arr) {
    if (isValidElement(child) && (child.type as unknown as { displayName?: string })?.displayName === displayName) {
      matched = child;
    } else {
      rest.push(child);
    }
  }
  return [matched, rest] as const;
}

export type ModalVariantProps = Exclude<RecipeVariantProps<typeof modalRecipe>, undefined>;
